import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { format, parseISO } from "date-fns";
import {
  MessageSquare, Activity, StickyNote, Stethoscope, ScanSearch,
  Search, Pill, FileText, Calendar, FileEdit,
} from "lucide-react";

import { usePrescriptionForm } from "@/hooks/usePrescriptionForm";
import { usePrescriptionSections } from "@/hooks/usePrescriptionSections";
import { usePrescriptionLayout } from "@/hooks/usePrescriptionLayout";
import FormSectionWrapper from "@/components/PrescriptionForm/FormSectionWrapper";
import FormActionBar from "@/components/PrescriptionForm/StickyBar";
import { Prescription, PrescriptionDataTransformer, PrescriptionFormData } from "@/types/prescription";
import { Patient } from "@/types";
import { Doctor } from "@/types/doctor";

import ComplaintsGrid from "@/components/PrescriptionFormSections/ComplaintsGrid";
import VitalsForm from "@/components/PrescriptionFormSections/VitalsForm";
import QuickNotesSection from "@/components/PrescriptionFormSections/QuickNotesSection";
import DiagnosisSection from "@/components/PrescriptionFormSections/DiagnosisSection";
import ExaminationSection from "@/components/PrescriptionFormSections/ExaminationSection";
import InvestigationsGrid from "@/components/PrescriptionFormSections/InvestigationsGrid";
import MedicineListForm from "@/components/PrescriptionFormSections/MedicineListForm";
import AdviceSection from "@/components/PrescriptionFormSections/AdviceSection";
import NextVisitSection from "@/components/PrescriptionFormSections/NextVisitSection";
import CustomFieldSection from "@/components/PrescriptionFormSections/CustomFieldSection";
import AttachmentsSection from "@/components/PrescriptionFormSections/AttachmentsSection";
import { StagedAttachment } from "@/components/PrescriptionFormSections/PendingAttachments";

import { RxTemplatesContext, useRxTemplatesProvider } from "@/components/Template/RxTemplates";
import TemplateTip from "@/components/PrescriptionForm/TemplateTip";
import PrintPrescriptionPreviewModal from "@/components/Prescription/PrintPrescriptionPreviewModal";
import { uploadPatientDocuments } from "@/lib/patientDocumentsApi";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormSection {
  id: string;
  icon: React.ReactNode;
  label: string;
  content: React.ReactNode;
  required?: boolean;
  block?: boolean;
}

/** Extra footer actions handed to a parent that renders its own action bar */
export interface RxFormActions {
  preview: () => void;
  submitAndPrint: () => void;
  /** Cancel: confirms when there are changes, drops the saved draft, then calls onCancel */
  cancel: () => void;
}

interface PrescriptionFormProps {
  mode?: "create" | "edit";
  patientId: string;
  initialData?: Partial<PrescriptionFormData>;
  /** Called after a save; with the saved prescription when the doctor chose "Save & print" */
  onSaved?: (printRx?: Prescription) => void;
  onCancel?: () => void;
  showStickyBar?: boolean;
  onFormReady?: (submitFn: () => void, hasChanges: boolean, isSubmitting: boolean, hasValidChanges: boolean, attachmentsSlot: React.ReactNode, pendingAttachmentsCount: number, summary: string, actions: RxFormActions) => void;
  patient?: Patient;
  doctor?: Doctor;
  /** Right-hand column beside the form (the prescription pad's ContextPanel); rendered inside the form context. The caller decides when there is room. */
  aside?: React.ReactNode;
  /** Width of the aside column in px (420 as the mockup; narrower on laptop screens) */
  asideWidth?: number;
  /** The patient's last prescription with medicines: offers "Last Rx" beside the medicine templates */
  lastPrescription?: Prescription;
}

// Sections a doctor may opt to hide via Settings → Prescription Layout.
// Complaints, Diagnosis, and Medications are always shown.
const HIDEABLE_SECTIONS = new Set(["vitals", "examination", "investigations", "advice", "notes", "next-visit"]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dobToAgeMonths(dob: string | undefined): number | undefined {
  if (!dob) return undefined;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return undefined;
  const now = new Date();
  const months =
    (now.getFullYear() - birth.getFullYear()) * 12 +
    (now.getMonth() - birth.getMonth());
  return now.getDate() >= birth.getDate() ? months : months - 1;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PrescriptionForm({
                                           mode = "create",
                                           patientId,
                                           initialData,
                                           onSaved,
                                           onCancel,
                                           showStickyBar = true,
                                           onFormReady,
                                           patient,
                                           doctor,
                                           aside,
                                           asideWidth = 420,
                                           lastPrescription,
                                         }: PrescriptionFormProps) {

  const patientAgeMonths = dobToAgeMonths(patient?.date_of_birth);
  const specialty        = doctor?.specialty ?? "General Medicine";

  // ── Attachments staged before the prescription has an id ───────────────────
  // (see AttachmentsSection / PendingAttachments). Flushed once save succeeds
  // and a real prescription_id exists to link them to.

  const pendingAttachmentsRef = useRef<StagedAttachment[]>([]);
  // Reactive twin of the ref above — the ref alone can't drive onFormReady
  // (which needs to notify the parent when the count actually changes so it
  // can warn on Cancel; a plain ref update doesn't trigger anything).
  const [pendingAttachmentsCount, setPendingAttachmentsCount] = useState(0);

  // "Save & print": remembered until the save finishes, then the saved prescription goes to onSaved
  const printAfterSaveRef = useRef(false);
  const getValuesRef = useRef<() => PrescriptionFormData>(() => ({} as PrescriptionFormData));

  const handleSaved = useCallback(async (prescriptionId?: string) => {
    const printRx = printAfterSaveRef.current && prescriptionId
      ? { ...PrescriptionDataTransformer.toApiPayload(getValuesRef.current()), id: prescriptionId }
      : undefined;
    printAfterSaveRef.current = false;
    const pending = pendingAttachmentsRef.current;
    pendingAttachmentsRef.current = [];
    setPendingAttachmentsCount(0);

    if (prescriptionId && pending.length > 0) {
      let createdCount = 0;
      let failedCount = 0;
      for (const item of pending) {
        try {
          const result = await uploadPatientDocuments(patientId, { prescriptionId, ...item.meta, files: item.files });
          createdCount += result.created.length;
          failedCount += result.errors.length;
        } catch {
          failedCount += item.files.length;
        }
      }
      if (createdCount > 0) {
        toast({ title: `Attached ${createdCount} document${createdCount > 1 ? "s" : ""}` });
      }
      if (failedCount > 0) {
        toast({
          title: `${failedCount} attachment${failedCount > 1 ? "s" : ""} failed to upload`,
          description: "The prescription itself was saved — you can retry attaching from the prescriptions list.",
          variant: "destructive",
        });
      }
    }

    onSaved?.(printRx);
  }, [patientId, onSaved]);

  // ── Form hooks ──────────────────────────────────────────────────────────────

  const formHook     = usePrescriptionForm({ mode, patientId, initialData, onSaved: handleSaved, onCancel, patientAllergies: patient?.allergies });
  // Latest cancel handler for the parent's action bar (it changes as hasChanges does)
  const cancelRef    = useRef(formHook.handleCancel);
  cancelRef.current  = formHook.handleCancel;
  getValuesRef.current = formHook.getValues as () => PrescriptionFormData;
  const sectionsHook = usePrescriptionSections();
  const rxTemplates  = useRxTemplatesProvider(formHook.form);

  // Preview: the print layout of what's on the form now, nothing saved
  const [previewRx, setPreviewRx] = useState<Prescription | null>(null);
  const submitFn = formHook.submitForm;
  const submit = useCallback(() => { printAfterSaveRef.current = false; submitFn(); }, [submitFn]);
  const actions = useMemo<RxFormActions>(() => ({
    preview: () => setPreviewRx(PrescriptionDataTransformer.toApiPayload(getValuesRef.current())),
    submitAndPrint: () => { printAfterSaveRef.current = true; submitFn(); },
    cancel: () => cancelRef.current(),
  }), [submitFn]);
  const { layout: prescriptionLayout } = usePrescriptionLayout();

  // Rendered once, placed next to Save — either in PrescriptionForm's own
  // FormActionBar (showStickyBar=true) or handed to the parent page via
  // onFormReady to render in its own externally-owned action bar
  // (showStickyBar=false, e.g. PatientDetailPage's create-prescription flow).
  // Memoized so its reference (and therefore AttachmentsSection's own mounted
  // state — dialog open/closed, staged count) stays stable across the
  // frequent re-renders this form gets from watch()-driven field updates.
  const attachmentsElement = useMemo(() => (
    <AttachmentsSection
      patientId={patientId}
      prescriptionId={mode === "edit" ? initialData?.id : undefined}
      onPendingChange={(items) => {
        pendingAttachmentsRef.current = items;
        setPendingAttachmentsCount(items.length);
      }}
    />
  ), [patientId, mode, initialData?.id]);

  // Stable ref for onFormReady — avoids re-running the effect when parent
  // passes a new inline function reference on every render
  const onFormReadyRef = useRef(onFormReady);
  useEffect(() => { onFormReadyRef.current = onFormReady; }, [onFormReady]);

  // "3 medicines · follow-up Sat, 04 Oct" — shown in the action bar
  const medicineCount = (formHook.watchedValues.medicines ?? []).filter((m: any) => m?.product?.trim()).length;
  const nextVisitDate: string = formHook.watchedValues.next_visit?.date || "";
  const summary = [
    medicineCount > 0 && `${medicineCount} medicine${medicineCount > 1 ? "s" : ""}`,
    nextVisitDate && `follow-up ${format(parseISO(nextVisitDate), "EEE, dd MMM")}`,
  ].filter(Boolean).join(" · ");

  useEffect(() => {
    onFormReadyRef.current?.(submit, formHook.hasChanges, formHook.isSubmitting, Boolean(formHook.hasValidChanges), attachmentsElement, pendingAttachmentsCount, summary, actions);
  }, [submit, actions, formHook.hasChanges, formHook.isSubmitting, formHook.hasValidChanges, attachmentsElement, pendingAttachmentsCount, summary]);

  // ── Section clearing ────────────────────────────────────────────────────────
  // One click empties the whole section; no confirmation, the toast has Undo.

  const clearSection = useCallback((sectionId: string, label: string) => {
    const { setValue, getValues } = formHook.form;
    // Pallor / icterus / edema are kept in vitals but shown under Examination; each section clears its own
    const SIGNS = ["pallor", "icterus", "edema"];
    if (sectionId === "vitals" || sectionId === "examination") {
      const before = { vitals: structuredClone(getValues("vitals")), examination: getValues("examination") };
      const vitals = Object.fromEntries(Object.entries(before.vitals || {})
        .filter(([key]) => SIGNS.includes(key) === (sectionId === "vitals")));
      setValue("vitals", vitals as any, { shouldDirty: true });
      if (sectionId === "examination") setValue("examination", "", { shouldDirty: true });
      toast({
        title: `Cleared ${label.toLowerCase()}`,
        action: <ToastAction altText="Undo" onClick={() => {
          setValue("vitals", before.vitals, { shouldDirty: true });
          setValue("examination", before.examination, { shouldDirty: true });
        }}>Undo</ToastAction>,
      });
      return;
    }
    const [field, empty]: [string, unknown] =
        sectionId === "complaints"     ? ["complaints", []] :
        sectionId === "diagnosis"      ? ["diagnosis", ""] :
        sectionId === "medicines"      ? ["medicines", []] :
        sectionId === "investigations" ? ["investigations", []] :
        sectionId === "advice"         ? ["advice", ""] :
        sectionId === "notes"          ? ["quick_notes", ""] :
        sectionId === "next-visit"     ? ["next_visit", {}] :
        [`custom_fields.${sectionId}`, ""];
    const before = structuredClone(getValues(field as any));
    setValue(field as any, empty as any, { shouldDirty: true });
    toast({
      title: `Cleared ${label.toLowerCase()}`,
      action: <ToastAction altText="Undo" onClick={() => setValue(field as any, before, { shouldDirty: true })}>Undo</ToastAction>,
    });
  }, [formHook.form]);

  // ── Sections config — memoized so JSX content elements are stable ───────────

  // Section order: Complaints → Vitals → Examination → Diagnosis → Medications → Investigations → Advice → Notes → Next Visit; custom fields after the section set in Settings, else last
  const sections: FormSection[] = useMemo(() => {
    const hidden = new Set(prescriptionLayout.hiddenSections);

    const baseSections: FormSection[] = [
      {
        id: "complaints", icon: <MessageSquare className="w-3.5 h-3.5" />,
        label: "Complaints", content: <ComplaintsGrid specialty={specialty} patientAgeMonths={patientAgeMonths} patientSex={patient?.gender} />,
        required: false,
      },
      {
        id: "vitals", icon: <Activity className="w-3.5 h-3.5" />,
        label: "Vitals",
        content: <VitalsForm patientAgeMonths={patientAgeMonths} patient={patient} prefillTodayVitals={mode === "create"} />,
      },
      {
        id: "examination", icon: <ScanSearch className="w-3.5 h-3.5" />,
        label: "Examination", content: <ExaminationSection />,
      },
      {
        id: "diagnosis", icon: <Stethoscope className="w-3.5 h-3.5" />,
        label: "Diagnosis",
        content: <DiagnosisSection specialty={specialty} patientAgeMonths={patientAgeMonths} patientSex={patient?.gender} />,
        required: false,
      },
      {
        id: "medicines", icon: <Pill className="w-3.5 h-3.5" />,
        label: "Medicines", block: true,
        content: <MedicineListForm patient={patient} interactionAlertsRef={formHook.interactionAlertsRef} lastPrescription={lastPrescription} />,
      },
      {
        id: "investigations", icon: <Search className="w-3.5 h-3.5" />,
        label: "Tests",
        content: <InvestigationsGrid specialty={specialty} patientAgeMonths={patientAgeMonths} patient={patient} />,
      },
      {
        id: "advice", icon: <FileText className="w-3.5 h-3.5" />,
        label: "Advice", content: <AdviceSection />,
      },
      {
        id: "notes", icon: <StickyNote className="w-3.5 h-3.5" />,
        label: "Quick notes", content: <QuickNotesSection />,
      },
      {
        id: "next-visit", icon: <Calendar className="w-3.5 h-3.5" />,
        label: "Follow-up", content: <NextVisitSection />,
      },
    ];

    const customFields = prescriptionLayout.customFields.filter((cf) => cf.enabled && cf.label.trim());
    const customSection = (cf: (typeof customFields)[number]): FormSection => ({
      id: cf.key,
      icon: <FileEdit className="w-3.5 h-3.5" />,
      label: cf.label,
      content: <CustomFieldSection fieldKey={cf.key} />,
    });

    // Each custom field follows the section chosen in Settings (unplaced ones go last). It keeps
    // that place when the section itself is hidden.
    const ordered = baseSections.flatMap((section) => [
      ...(HIDEABLE_SECTIONS.has(section.id) && hidden.has(section.id) ? [] : [section]),
      ...customFields.filter((cf) => cf.after === section.id).map(customSection),
    ]);
    const placed = new Set(baseSections.map((s) => s.id));
    return [...ordered, ...customFields.filter((cf) => !cf.after || !placed.has(cf.after)).map(customSection)];
  }, [patientAgeMonths, specialty, patient, prescriptionLayout, mode, lastPrescription]);

  const getSectionErrors = useCallback((sectionId: string): boolean => {
    switch (sectionId) {
      case "complaints":     return !!formHook.errors.complaints;
      case "vitals":         return !!formHook.errors.vitals;
      case "examination":    return !!formHook.errors.examination;
      case "diagnosis":      return !!formHook.errors.diagnosis;
      case "medicines":      return !!formHook.errors.medicines;
      case "investigations": return !!formHook.errors.investigations;
      case "advice":         return !!formHook.errors.advice;
      case "notes":          return !!formHook.errors.quick_notes;
      case "next-visit":     return !!formHook.errors.next_visit;
      default:               return false;
    }
  }, [formHook.errors]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
      <FormProvider {...formHook.form}>
        <RxTemplatesContext.Provider value={rxTemplates}>
        <div
            className={cn(aside && "grid gap-4 items-start")}
            style={aside ? { gridTemplateColumns: `minmax(0,1fr) ${asideWidth}px` } : undefined}
        >
        {/* 20px from the header to the first field, as in the mockup (sections add their own 7px) */}
        <div className="min-w-0 pt-[13px]">

          {sections.map((section) => (
              <React.Fragment key={section.id}>
                <FormSectionWrapper
                    section={section}
                    hasErrors={getSectionErrors(section.id)}
                    onClear={sectionsHook.getSectionStatus(section.id, formHook.watchedValues) ? () => clearSection(section.id, section.label) : undefined}
                />
                {section.id === "medicines" && (
                    <TemplateTip
                        medicineCount={sectionsHook.getSectionCount("medicines", formHook.watchedValues) ?? 0}
                        onSave={() => rxTemplates.requestSave("medicine")}
                    />
                )}
              </React.Fragment>
          ))}
        </div>

          {aside && <div className="sticky top-[calc(var(--topbar-height,56px)+var(--patient-header-height,0px)+1rem)]">{aside}</div>}

          {showStickyBar && (
              <FormActionBar
                  // Bleed out of the page's px-4/sm:px-6 gutter so the bar spans the same width as the header above
                  className="-mx-4 sm:-mx-6 [&>div]:px-4 sm:[&>div]:px-6"
                  hasChanges={formHook.hasChanges}
                  hasValidChanges={Boolean(formHook.hasValidChanges)}
                  isSubmitting={formHook.isSubmitting}
                  mode={formHook.mode}
                  onSubmit={submit}
                  onCancel={formHook.handleCancel}
                  attachmentsSlot={attachmentsElement}
                  summary={summary}
                  // Preview hidden for now: onPreview={actions.preview}
                  onSubmitAndPrint={actions.submitAndPrint}
              />
          )}

          <PrintPrescriptionPreviewModal open={!!previewRx} prescription={previewRx} onClose={() => setPreviewRx(null)} />

        </div>
        </RxTemplatesContext.Provider>
      </FormProvider>
  );
}